import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ClipboardCheck, Ruler, AlertTriangle,
  CheckCircle2, XCircle, ChevronRight, Gauge,
} from 'lucide-react';
import { qualityDashboardService } from '../services/api';

const KPI_CARD = "rounded-xl border bg-white p-5 shadow-sm";

function StatusDot({ color }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`}></span>;
}

export default function QualityDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await qualityDashboardService.get();
      setData(r.data);
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="py-16 text-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }
  if (!data) return <p className="text-center py-8 text-slate-500">Errore caricamento dati</p>;

  const { controlli_eseguiti: ctrl, tarature: tar } = data;
  const okPct = ctrl.today.total > 0 ? Math.round((ctrl.today.ok / ctrl.today.total) * 100) : 0;
  const tarUrgent = tar.expired + tar.in_30d;
  const totalCal = tar.expired + tar.in_30d + tar.in_60d + tar.in_90d;

  return (
    <div className="space-y-6 pb-12" data-testid="quality-dashboard-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <Gauge className="w-8 h-8 text-slate-700" />
          Cruscotto Qualità
        </h1>
        <p className="text-sm text-slate-500 mt-1">Snapshot operativo: controlli odierni e scadenze tarature.</p>
      </div>

      {/* ─── KPI Top Row ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{/* removed 8D KPI card */}
        {/* Controlli oggi */}
        <Card data-testid="card-controls-today">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2 text-slate-600">
                <ClipboardCheck className="w-5 h-5" />
                <span className="text-sm font-semibold uppercase tracking-wide">Controlli Oggi</span>
              </div>
              <Link to="/qms-checks" className="text-xs text-slate-500 hover:text-slate-900">Vedi →</Link>
            </div>
            <div className="text-4xl font-bold text-slate-900">{ctrl.today.total}</div>
            <div className="mt-2 flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> {ctrl.today.ok} OK
              </span>
              <span className="flex items-center gap-1.5 text-red-700">
                <XCircle className="w-4 h-4" /> {ctrl.today.nok} NOK
              </span>
            </div>
            {ctrl.today.total > 0 && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Conformità</span><span className="font-bold">{okPct}%</span>
                </div>
                <Progress value={okPct} className={okPct >= 95 ? '' : 'opacity-80'} />
              </div>
            )}
            <p className="text-xs text-slate-400 mt-3">Ultimi 30 gg: {ctrl.last_30d.total} tot · {ctrl.last_30d.nok} NOK</p>
          </CardContent>
        </Card>

        {/* Tarature in scadenza */}
        <Card data-testid="card-calibrations">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2 text-slate-600">
                <Ruler className="w-5 h-5" />
                <span className="text-sm font-semibold uppercase tracking-wide">Tarature in Scadenza</span>
              </div>
              <Link to="/instruments" className="text-xs text-slate-500 hover:text-slate-900">Vedi →</Link>
            </div>
            <div className="text-4xl font-bold text-slate-900">{totalCal}</div>
            <div className="mt-2 space-y-1.5">
              {tar.expired > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-red-700 font-medium">
                    <AlertTriangle className="w-4 h-4" /> Scadute
                  </span>
                  <Badge className="bg-red-100 text-red-800 border border-red-300">{tar.expired}</Badge>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-amber-700">
                  <StatusDot color="bg-amber-500" /> ≤ 30 gg
                </span>
                <span className="font-semibold">{tar.in_30d}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600">
                  <StatusDot color="bg-slate-400" /> 31–60 gg
                </span>
                <span className="font-semibold">{tar.in_60d}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-500">
                  <StatusDot color="bg-slate-300" /> 61–90 gg
                </span>
                <span className="font-semibold">{tar.in_90d}</span>
              </div>
            </div>
            {tarUrgent > 0 && (
              <p className="text-xs text-red-600 mt-3 font-medium">⚠ {tarUrgent} strumento/i richiede attenzione</p>
            )}
          </CardContent>
        </Card>

        {/* Report 8D card removed (modulo dismesso) */}
      </div>

      {/* ─── Detail Lists ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Upcoming Calibrations List */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Ruler className="w-4 h-4" />Strumenti in scadenza (≤ 90 gg)</CardTitle></CardHeader>
          <CardContent>
            {(tar.upcoming || []).length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nessuno strumento in scadenza nei prossimi 90 giorni</p>
            ) : (
              <div className="space-y-2">
                {tar.upcoming.slice(0, 10).map(s => {
                  const today = new Date().toISOString().slice(0, 10);
                  const expired = s.next_calibration_date < today;
                  return (
                    <Link key={s.id} to={`/instruments/${s.id}`} className="block" data-testid={`upcoming-inst-${s.id}`}>
                      <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${expired ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-mono font-semibold text-sm">{s.code}</p>
                          <p className="text-xs text-slate-500 truncate">{s.description}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <Badge className={expired ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-amber-100 text-amber-800 border border-amber-300'}>
                            {s.next_calibration_date}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent 8D card removed (modulo dismesso) */}
      </div>
    </div>
  );
}
